import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Root-level error boundary.
 *
 * Without this, React 18 unmounts the entire root tree when a render-time
 * error is thrown unhandled — the user sees a blank page with no signal
 * for what went wrong. That was the symptom hunted on rockwell.digavouch.com
 * 2026-06-07 (AppShell + CookieConsent + Dashboard all unmounted after login
 * with no console error visible).
 *
 * With this boundary in place:
 *   - The error is caught and logged to console with full stack +
 *     component stack so future repros are debuggable.
 *   - A visible fallback UI replaces the crashed subtree so the user
 *     knows something went wrong (and we know during testing too).
 *   - The rest of the React tree above the boundary (providers, router)
 *     stays mounted, so a "Reload" affordance doesn't have to re-init
 *     auth from scratch.
 *
 * Standard React class-component pattern — there is no hooks equivalent
 * for error boundaries as of React 19.
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log both to console so engineers can pull from DevTools when an
    // operator reports a crash. The errorInfo.componentStack is the
    // human-readable React component tree at throw time — far more
    // useful than error.stack alone, especially with the source maps
    // emitted by vite.config.ts.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught render error:", error);
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] component stack:", errorInfo.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    // Quiet, operator-friendly fallback. The error message + stack + component
    // stack are still logged to the browser console by componentDidCatch
    // above — engineers can pull them from DevTools when an operator reports
    // a crash. We deliberately don't dump the raw stack on screen: it's noise
    // for operators and useful state can leak into screenshots. If a richer
    // "show details" affordance is needed later, add a collapsible toggle.
    return (
      <div
        role="alert"
        style={{
          maxWidth: "32rem",
          margin: "4rem auto",
          padding: "1.5rem",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
          color: "#0f172a",
        }}
      >
        <h1 style={{ fontSize: "1.125rem", fontWeight: 600, margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ marginTop: "0.5rem", color: "#475569", fontSize: "0.875rem" }}>
          The page hit an unexpected error. Try reloading. If it keeps
          happening, send the URL and a rough time to support so we can
          investigate.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            marginTop: "1.25rem",
            padding: "0.5rem 1.25rem",
            background: "#1e293b",
            color: "white",
            border: 0,
            borderRadius: "0.375rem",
            cursor: "pointer",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
